import React from 'react';

import { Image, Link, Scrollbar } from 'components/ui';
import { isArrayEmpty } from 'helpers/ArrayHelpers';
import { parseCategoryImageUrl } from 'helpers/CategoryHelpers';
import { useInView } from 'hooks/common/useInView';
import { useTranslation } from 'hooks/common/useTranslation';
import { useAnalytics } from 'hooks/useAnalytics';

import type { ISearchSuggestionCategory, ISearchSuggestions } from 'types';

import styles from './SearchPageSuggestedCategories.module.scss';

interface Props {
  suggestedCategories?: ISearchSuggestions
}

const SearchPageSuggestedCategories = (props: Props) => {
  const { suggestedCategories } = props;

  const { t } = useTranslation();

  const analytics = useAnalytics();

  // Handlers

  const handleClick = (imageUrl: string) => {
    if (imageUrl) {
      analytics.selectProductCategoryItem(parseCategoryImageUrl(imageUrl));
    }
  };

  const { categories } = suggestedCategories || {};

  if (isArrayEmpty(categories)) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        {t('SEARCH.SUGGESTED_CATEGORIES_TITLE')}
      </div>
      <Scrollbar
        className={styles.scrollbar}
        autoHide={false}
        forceVisible="y"
        draggable
      >
        <div className={styles.categories}>
          {
            categories.map((category, index) => {
              const { code } = category || {};
              return (
                <SearchSuggestionCategory
                  key={`${code}-${index}`}
                  category={category}
                  handleClick={handleClick}
                />
              );
            })
          }
        </div>
      </Scrollbar>
    </div>
  );
};

interface SearchSuggestionCategoryProps {
  category: ISearchSuggestionCategory,
  handleClick: (image: string) => void
}

const SearchSuggestionCategory = (props: SearchSuggestionCategoryProps) => {
  const {
    category,
    handleClick
  } = props;

  const {
    code, name, url, gridImage
  } = category || {};

  // Hooks

  const analytics = useAnalytics();

  const { observe } = useInView({
    threshold: 0.75,
    onEnter: ({ unobserve }) => {
      if (gridImage) {
        analytics.viewProductCategoryItem(parseCategoryImageUrl(gridImage));
      }
      unobserve();
    }
  });

  // Render

  return (
    <div
      ref={observe}
      className={styles.category}
      onClick={() => handleClick(gridImage)}
    >
      <Link passHref href={url} prefetch={false}>
        <a className={styles.categoryContent}>
          <Image className={styles.categoryImage} src={gridImage} width={40} height={40} />
          <span>{name}</span>
        </a>
      </Link>
    </div>
  );
};

export default SearchPageSuggestedCategories;
